// client/src/components/secure/navConfig.js — the staff sidebar, as data (docs/ui-tidy.md).
//
// Seven sections in a fixed order. Each item may carry a `show(user)` predicate; the
// predicates are the same ones the menu used before the tidy (never loosen them).
// `sub` is a small caption inside a long section; `keywords` lets the menu search find an
// item by its old name. Plain ES module with no imports, so scripts/test-nav.js can load it.
// (This replaces wave2Nav.js: add new menu items here.)

export const isMB = (u) => !!u?.isManagingBroker;
export const isOfficeManager = (u) => u?.staffRole === 'Office Manager';
export const isExaminer = (u) => u?.role === 'examiner';
const mbOrOm = (u) => isMB(u) || isOfficeManager(u);
// Finance READ matches the server's financeRead guard (routes/accounting.js, commissions.js).
const financeReader = (u) => isMB(u) || isOfficeManager(u) || u?.staffRole === 'Bookkeeper';
const agentMbOrOm = (u) => !!u?.agentId || isMB(u) || isOfficeManager(u);
const STAFF_ROLES = ['Office Manager', 'Bookkeeper', 'Staff', 'Read Only'];
const assistantUser = (u) => !isExaminer(u) && (!!u?.agentId || isMB(u) || (u?.role === 'admin' && STAFF_ROLES.includes(u?.staffRole)));
// O2: the same test as the server's requirePmAccess (middleware/pmAuthority.js): managing
// broker, an admin login, or a live property-management grant (the session's pmGrant claim).
export const hasPmAccess = (u) => isMB(u) || u?.role === 'admin' || isExaminer(u) || !!u?.pmGrant;
// Agent access review (owner, 24 Oct 2026: "make the app and their sign-ins privilege restricted
// to the capabilities"). An agent login is a licensee who is not the managing broker; team leads
// are agent logins too and keep their team scope (and the Teams page). These predicates only
// ever HIDE office business from agent logins; they never show anything new to anyone else.
export const isAgentLogin = (u) => u?.role === 'licensee' && !isMB(u);
export const isTeamLead = (u) => isAgentLogin(u) && !!u?.isTeamLead;
const notAgentLogin = (u) => !isAgentLogin(u);
const notPlainAgent = (u) => !isAgentLogin(u) || isTeamLead(u);

// Job access profiles (owner, 26 Oct 2026; lib/jobAccess.js on the server). A login given a
// job profile carries `areas` in its session; every item below names its area, and such a
// login sees an item only when the item's area is one of theirs, or 'always'. This only ever
// HIDES: the show predicates above still apply first. No profile = no `areas` = the menu as
// before; the managing broker and examiners are never narrowed. scripts/test-job-access.js
// keeps these tags equal to the server's catalogue.
export const hasJobProfile = (u) => Array.isArray(u?.areas) && !isMB(u) && !isExaminer(u);
const areaOk = (u, it) => !hasJobProfile(u) || it.area === 'always' || u.areas.includes(it.area);
// Owner decisions, 26 Oct 2026 (lib/jobAccess WIDENING_AREAS): the only places a profile
// shows MORE — the website chat inbox, and the Tax Center and Payroll to view — and only for a
// staff login, matching the server's orJobGrant on exactly those routes.
const WIDENING = ['website_chat', 'tax_center', 'payroll'];
export const jobGrants = (u, area) => WIDENING.includes(area) && hasJobProfile(u) && u?.role === 'admin' && u.areas.includes(area);
const itemOk = (u, it) => (it.show ? !!it.show(u) : true) && areaOk(u, it);

export const SECTIONS = [
    // One tap on the phone app: an agent's two searches first (owner, 24 Oct 2026: "a portal where
    // they can do MLS type searches … and the zoning searches"). Everyone else finds Property
    // Search and Zoning Search under Listings & Marketing, as before.
    {
        title: 'Searches',
        show: isAgentLogin,
        items: [
            { to: '/dashboard/property-search', label: 'Property Search', area: 'property_search', keywords: 'mls listings search idx homes commercial for sale lease' },
            { to: '/dashboard/property-search?tab=zoning', label: 'Zoning Search', area: 'property_search', keywords: 'zoning what can go where business use by right' },
            { to: '/dashboard/property-search?tab=saved', label: 'Saved Searches', area: 'property_search', keywords: 'saved search client alerts' },
            { to: '/dashboard/property-search?tab=matches', label: 'New Matches', area: 'property_search', keywords: 'matches new listings clients' },
        ],
    },
    {
        title: 'Today',
        items: [
            { to: '/dashboard', label: 'Overview', area: 'always', end: true, keywords: 'dashboard home today' },
            { to: '/dashboard/tasks', label: 'Tasks', area: 'always' },
            { to: '/dashboard/my-tc-items', label: 'My TC Items', area: 'transactions_forms', keywords: 'transaction coordinator checklist closing steps' },
            { to: '/dashboard/task-automation', label: 'Task Inbox & Templates', area: 'always', keywords: 'automation' },
            { to: '/dashboard/mailroom', label: 'Mailroom', area: 'mailroom', keywords: 'mail' },
            { to: '/dashboard/desk-drafts', label: 'Assistant Drafts', area: 'always', keywords: 'brokerage desk' },
            { to: '/dashboard/my-plan', label: 'My Plan & Coverage', area: 'sales_territory', show: agentMbOrOm },
            // Staff assistant (21 Oct 2026): the same audience as the server's assistantUser gate
            // (routes/chatAdmin.js) — agents, the managing broker and the staff roles; never examiners.
            { to: '/dashboard/assistant', label: 'Ask the Assistant', area: 'always', show: assistantUser, keywords: 'ai help question how do i staff assistant' },
        ],
    },
    {
        title: 'Clients & Deals',
        items: [
            { to: '/dashboard/clients', label: 'Clients', area: 'clients' },
            { to: '/dashboard/transactions', label: 'Transactions', area: 'transactions_forms', keywords: 'deals milestones' },
            { to: '/dashboard/forms', label: 'Forms Portal', area: 'transactions_forms', keywords: 'offers agreements buyer agency listing agreement' },
            { to: '/dashboard/crm', label: 'CRM', area: 'crm_leads', noTab: true, sub: 'CRM & leads', keywords: 'crm dashboard' },
            { to: '/dashboard/crm?tab=contacts', label: 'Contacts', area: 'crm_leads', keywords: 'crm' },
            { to: '/dashboard/crm?tab=pipeline', label: 'Pipeline', area: 'crm_leads', keywords: 'crm' },
            { to: '/dashboard/crm?tab=follow-ups', label: 'Follow-ups', area: 'crm_leads', keywords: 'crm' },
            { to: '/dashboard/hot-leads', label: 'Hot Leads', area: 'front_desk', keywords: 'leads' },
            { to: '/dashboard/my-website-leads', label: 'My Website Leads', area: 'front_desk', show: agentMbOrOm, keywords: 'website chat assistant' },
            { to: '/dashboard/website-chat', label: 'Website Chat Inbox', area: 'website_chat', show: (u) => mbOrOm(u) || jobGrants(u, 'website_chat'), keywords: 'chat inbox settings' },
            { to: '/dashboard/lead-routing', label: 'Lead Routing', area: 'sales_territory', show: agentMbOrOm, sub: 'Territory', keywords: 'sales' },
            // Territory set-up is the managing broker's / office manager's; an agent works their own
            // through Lead Routing and My Plan & Coverage.
            { to: '/dashboard/territories', label: 'Territories', area: 'sales_territory', show: notAgentLogin, keywords: 'sales' },
        ],
    },
    {
        title: 'Listings & Marketing',
        items: [
            { to: '/dashboard/properties', label: 'Properties', area: 'listings', keywords: 'listings' },
            // Agent logins have both under Searches at the top of the menu instead.
            { to: '/dashboard/property-search', label: 'Property Search', area: 'property_search', show: notAgentLogin },
            { to: '/dashboard/property-search?tab=zoning', label: 'Zoning Search', area: 'property_search', show: (u) => notAgentLogin(u) && !isExaminer(u), keywords: 'zoning what can go where business use by right' },
            { to: '/dashboard/listing-submissions', label: 'Listing Submissions', area: 'listings', keywords: 'owner listings' },
            { to: '/dashboard/open-houses', label: 'Open Houses', area: 'showings_calendar' },
            { to: '/dashboard/showings', label: 'Showings', area: 'showings_calendar', keywords: 'showing requests availability booking calendar feedback' },
            { to: '/dashboard/cma', label: 'CMA Builder', area: 'marketing', keywords: 'cma comparative market analysis broker price opinion bpo comps valuation pricing' },
            // The library is the office's research desk; agents search it through Zoning Search.
            { to: '/dashboard/zoning-library', label: 'Zoning Library', area: 'site_search', show: notAgentLogin, sub: 'Site search', keywords: 'site search zoning' },
            // Owner, 22 Oct 2026: the managing broker's own searches (routes/brokerSiteSearch.js: MB + admin logins).
            { to: '/dashboard/my-site-searches', label: 'My Site Searches', area: 'site_search', show: (u) => !isExaminer(u) && !!(isMB(u) || u?.role === 'admin'), keywords: 'site search by right school vocational cosmetology college broker sweep' },
            { to: '/dashboard/site-search-alerts', label: 'Site Search Alerts', area: 'site_search', show: (u) => !isExaminer(u) && !!(isMB(u) || u?.role === 'admin' || u?.staffRole), keywords: 'site search alert emails' },
            { to: '/dashboard/reviews', label: 'Reviews & Testimonials', area: 'marketing', sub: 'Marketing' },
            { to: '/dashboard/campaigns', label: 'Campaigns', area: 'marketing', show: agentMbOrOm, keywords: 'drip email text nurture newsletter follow-up sequence' },
            { to: '/dashboard/commercial-toolkit', label: 'Commercial Toolkit', area: 'marketing' },
            { to: '/dashboard/virtual-staging', label: 'Virtual Staging', area: 'marketing' },
            { to: '/dashboard/seo', label: 'Search & Social Metadata', area: 'marketing', show: isMB, keywords: 'seo' },
            { to: '/dashboard/social-media', label: 'Social Media', area: 'marketing', show: isMB, keywords: 'facebook instagram x twitter wechat posts' },
        ],
    },
    {
        title: 'Property Management',
        show: hasPmAccess,
        items: [
            { to: '/dashboard/property-management', label: 'PM Overview', area: 'pm_operations', end: true, keywords: 'property management overview' },
            { to: '/dashboard/pm-portfolio', label: 'Portfolio', area: 'pm_operations' },
            { to: '/dashboard/pm-onboarding', label: 'Client Onboarding', area: 'pm_operations', keywords: 'pm' },
            { to: '/dashboard/pm-owners', label: 'Clients & Books', area: 'pm_trust_books', keywords: 'owners' },
            { to: '/dashboard/pm-leasing', label: 'Leasing', area: 'pm_operations', sub: 'Leasing & upkeep' },
            { to: '/dashboard/pm-leasing?tab=applications', label: 'Tenant Screening', area: 'pm_operations', keywords: 'applications' },
            { to: '/dashboard/pm-commercial', label: 'Commercial Leasing', area: 'pm_operations', keywords: 'cam nnn reconciliation percentage rent options' },
            { to: '/dashboard/pm-leasing-responder', label: 'Leasing Responder (test)', area: 'pm_operations' },
            { to: '/dashboard/pm-maintenance', label: 'Maintenance & Vendors', area: 'pm_operations' },
            { to: '/dashboard/pm-deadlines', label: 'Compliance Calendar', area: 'pm_operations', sub: 'Trust & compliance', keywords: 'deadlines' },
            { to: '/dashboard/pm-trust-accounts', label: 'Trust Accounts', area: 'pm_trust_books' },
            { to: '/dashboard/pm-month-end', label: 'Month-End & Year-End', area: 'pm_trust_books' },
            { to: '/dashboard/pm-online-payments', label: 'Online Payments', area: 'pm_trust_books', keywords: 'stripe ach card rent autopay payout top-up' },
            { to: '/dashboard/pm-reports', label: 'Reports', area: 'pm_operations', keywords: 'owner statement p&l budget occupancy aging deposit register vendor' },
            { to: '/dashboard/pm-authority', label: 'Module Authority', area: 'pm_operations', show: isMB },
        ],
    },
    {
        title: 'Compliance & Records',
        items: [
            { to: '/dashboard/compliance', label: 'Compliance Hub', area: 'compliance', end: true },
            { to: '/dashboard/compliance/checklist', label: 'By Transaction', area: 'compliance', keywords: 'compliance checklist' },
            { to: '/dashboard/deal-compliance', label: 'Required Documents', area: 'compliance', keywords: 'deal compliance' },
            { to: '/dashboard/advertising', label: 'Ad Review', area: 'compliance', show: isMB, keywords: 'advertising' },
            // O3: matches the server's escrowRead (routes/escrow.js, escrowCompliance.js):
            // MB + Bookkeeper + Office Manager read; writes stay as the server has them.
            { to: '/dashboard/escrow', label: 'Escrow', area: 'escrow_trust', show: financeReader, sub: 'Escrow' },
            { to: '/dashboard/escrow-compliance', label: 'Escrow Compliance', area: 'escrow_trust', show: financeReader },
            // Matches the server: routes/contactCompliance.js reads are MB + Office Manager.
            { to: '/dashboard/contact-compliance', label: 'Contact Compliance', area: 'compliance', show: mbOrOm, keywords: 'dnc do not call tcpa consent opt-out' },
            { to: '/dashboard/document-library', label: 'Document Library', area: 'documents', sub: 'Documents & forms' },
            { to: '/dashboard/forms-cabinet', label: 'Forms Cabinet', area: 'documents' },
            { to: '/dashboard/operations', label: 'Doc Cabinet', area: 'documents', keywords: 'operations' },
            { to: '/dashboard/policy-center', label: 'Policy Center', area: 'always' },
            { to: '/dashboard/retention', label: 'Records Retention', area: 'compliance', show: isMB, sub: 'Records & inspection' },
            { to: '/dashboard/corporate-records', label: 'Corporate Records', area: 'corporate_records', show: isMB },
            { to: '/dashboard/corporate-book', label: 'Corporate Book', area: 'corporate_records', show: isMB, keywords: 'minute book' },
            { to: '/dashboard/desk-review', label: 'Monthly Broker Review', area: 'compliance', show: (u) => !!(u?.isManagingBroker || u?.staffRole === 'Office Manager' || u?.role === 'examiner'), keywords: 'brokerage desk' },
            { to: '/dashboard/examiner-access', label: 'Division Inspection Access', area: 'user_admin', show: isMB, keywords: 'examiner idfpr' },
        ],
    },
    {
        title: 'Office & People',
        items: [
            { to: '/dashboard/my-office', label: 'My Office', area: 'always', sub: 'Me' },
            { to: '/dashboard/my-onboarding', label: 'My Onboarding', area: 'always', show: (u) => !!(u?.agentId || u?.employeeId) },
            { to: '/dashboard/my-commissions', label: 'My Commissions', area: 'always', show: (u) => !!u?.agentId },
            { to: '/dashboard/file-cabinet', label: 'My File Cabinet', area: 'always' },
            { to: '/dashboard/training', label: 'Training & CE', area: 'always' },
            { to: '/dashboard/intranet', label: 'Office Intranet', area: 'always', sub: 'The office' },
            { to: '/dashboard/office-directory', label: 'Office Directory', area: 'always' },
            { to: '/dashboard/office-registry', label: 'Office Registry', area: 'always' },
            // The rosters are office administration; an agent's colleagues are in the Office Directory.
            { to: '/dashboard/agents', label: 'Agents', area: 'agents_roster', show: notAgentLogin, sub: 'People' },
            { to: '/dashboard/teams', label: 'Teams', area: 'agents_roster', show: notPlainAgent },
            { to: '/dashboard/employees', label: 'Employees', area: 'hr_people', show: notAgentLogin },
            // O11: the whole office for MB + Office Manager; everyone else their own record.
            { to: '/dashboard/license-tracking', label: 'License & CE', area: 'agents_roster', show: mbOrOm },
            { to: '/dashboard/license-tracking', label: 'My License & CE', area: 'always', show: (u) => !mbOrOm(u), keywords: 'license ce' },
            { to: '/dashboard/agent-production', label: 'Production', area: 'reports' },
            { to: '/dashboard/recruiting', label: 'Recruiting', area: 'recruiting_onboarding', show: mbOrOm },
            { to: '/dashboard/onboarding', label: 'Onboarding', area: 'recruiting_onboarding', show: mbOrOm },
            { to: '/dashboard/hr', label: 'HR Compliance', area: 'hr_people', show: mbOrOm },
            { to: '/dashboard/accounting', label: 'Accounting', area: 'accounting_ledger', show: financeReader, sub: 'Money' },
            { to: '/dashboard/commissions', label: 'Commissions', area: 'commissions', show: financeReader },
            { to: '/dashboard/payroll', label: 'Payroll', area: 'payroll', show: (u) => isMB(u) || jobGrants(u, 'payroll') },
            { to: '/dashboard/checks', label: 'Check Register', area: 'checks_expenses', show: isMB },
            { to: '/dashboard/expenses', label: 'Expenses', area: 'checks_expenses', show: isMB },
            { to: '/dashboard/tax-center', label: 'Tax Center', area: 'tax_center', show: (u) => isMB(u) || jobGrants(u, 'tax_center') },
        ],
    },
    {
        title: 'Admin & Settings',
        items: [
            { to: '/dashboard/command-center', label: 'Command Center', area: 'office_settings', show: mbOrOm },
            { to: '/dashboard/system-status', label: 'System Status', area: 'office_settings', show: mbOrOm },
            { to: '/dashboard/virtual-office-settings', label: 'Virtual Office Settings', area: 'office_settings', show: isMB },
            { to: '/dashboard/integrations', label: 'Integrations', area: 'office_settings', show: isMB },
            { to: '/dashboard/user-accounts', label: 'Login Accounts', area: 'user_admin', show: isMB },
            // Job access profiles (owner, 26 Oct 2026): which areas each office job sees.
            { to: '/dashboard/job-access', label: 'Job Access', area: 'user_admin', show: isMB, keywords: 'receptionist secretary bookkeeper accounting office manager profile permissions menu what they see' },
            { to: '/dashboard/desk-settings', label: 'Desk Settings', area: 'office_settings', show: mbOrOm, keywords: 'brokerage desk' },
            { to: '/dashboard/tc-templates', label: 'TC Checklist Templates', area: 'transactions_forms', show: mbOrOm, keywords: 'transaction coordinator checklist steps' },
            { to: '/dashboard/audit-log', label: 'Audit Log', area: 'user_admin', show: mbOrOm },
            { to: '/dashboard/change-password', label: 'Change Password', area: 'always', sub: 'My account' },
            { to: '/dashboard/security', label: 'My Security', area: 'always', keywords: 'two-factor 2fa' },
            { to: '/dashboard/passwordless', label: 'Password-free Sign-in', area: 'always', keywords: 'passkey tailscale trusted device fingerprint face login without password' },
            { to: '/dashboard/notifications', label: 'Phone Notifications', area: 'always', keywords: 'push alerts device mobile' },
            { to: '/dashboard/app', label: 'The BREC App', area: 'always', keywords: 'phone tablet install' },
        ],
    },
];

// A Division examiner sees only the record areas the inspection rule contemplates —
// deal files, escrow/special accounts, disclosures, policy, retention, and the licensee
// registry. Read-only; the server refuses writes. Same items and labels as before the
// tidy, in the same section order as the staff menu.
export const EXAMINER_SECTIONS = [
    {
        title: 'Today',
        items: [
            { to: '/dashboard', label: 'Overview', end: true },
            // An inspection account starts with no areas granted, so this is the first
            // screen that is any use: what has been allowed, and how to ask for the rest.
            { to: '/dashboard/my-access', label: 'My Access' },
        ],
    },
    {
        title: 'Transaction Records',
        items: [
            { to: '/dashboard/transactions', label: 'Transactions' },
            { to: '/dashboard/clients', label: 'Clients' },
            { to: '/dashboard/properties', label: 'Properties' },
        ],
    },
    {
        title: 'Property Management Trust',
        items: [
            { to: '/dashboard/property-management', label: 'Overview', end: true },
            { to: '/dashboard/pm-portfolio', label: 'Portfolio' },
            { to: '/dashboard/pm-onboarding', label: 'Client Onboarding' },
            { to: '/dashboard/pm-owners', label: 'Client Trust Books' },
            { to: '/dashboard/pm-leasing', label: 'Leases' },
            { to: '/dashboard/pm-commercial', label: 'Commercial Leases & CAM' },
            { to: '/dashboard/pm-maintenance', label: 'Maintenance & Vendors' },
            { to: '/dashboard/pm-deadlines', label: 'Compliance Calendar' },
            { to: '/dashboard/pm-trust-accounts', label: 'Trust Accounts & Master Log' },
            { to: '/dashboard/pm-online-payments', label: 'Online Payments' },
            { to: '/dashboard/pm-reports', label: 'Reports' },
        ],
    },
    {
        title: 'Compliance & Records',
        items: [
            { to: '/dashboard/compliance', label: 'Compliance Hub', end: true },
            { to: '/dashboard/compliance/checklist', label: 'By Transaction' },
            { to: '/dashboard/deal-compliance', label: 'Required Documents' },
            { to: '/dashboard/advertising', label: 'Advertising Review' },
            { to: '/dashboard/escrow', label: 'Escrow Accounts', sub: 'Escrow & special accounts' },
            { to: '/dashboard/escrow-compliance', label: 'Escrow Compliance' },
            { to: '/dashboard/retention', label: 'Records Retention', sub: 'Records' },
            { to: '/dashboard/contact-compliance', label: 'Contact Compliance' },
            { to: '/dashboard/document-library', label: 'Document Library' },
            { to: '/dashboard/forms-cabinet', label: 'Forms Cabinet' },
            { to: '/dashboard/operations', label: 'Doc Cabinet' },
            // Area desk_review: read-only, and only once the managing broker grants it.
            { to: '/dashboard/desk-review', label: 'Monthly Broker Review', sub: 'Brokerage desk' },
            { to: '/dashboard/audit-log', label: 'Audit Log' },
        ],
    },
    {
        title: 'Licensees & Supervision',
        items: [
            { to: '/dashboard/office-registry', label: 'Office Registry' },
            { to: '/dashboard/agents', label: 'Sponsored Licensees' },
            { to: '/dashboard/employees', label: 'Employees' },
            { to: '/dashboard/teams', label: 'Teams' },
            // O11: the whole office for MB + Office Manager; everyone else their own record.
            { to: '/dashboard/license-tracking', label: 'License & CE', show: mbOrOm },
            { to: '/dashboard/license-tracking', label: 'My License & CE', show: (u) => !mbOrOm(u), keywords: 'license ce' },
            { to: '/dashboard/training/records', label: 'Training Records' },
            { to: '/dashboard/policy-center', label: 'Policy Center' },
        ],
    },
    {
        title: 'My Account',
        items: [
            { to: '/dashboard/change-password', label: 'Change Password' },
            { to: '/dashboard/security', label: 'My Security' },
        ],
    },
];

/** The sections this user may see, each holding only its visible items (empty sections dropped). */
export function visibleSections(user) {
    const src = isExaminer(user) ? EXAMINER_SECTIONS : SECTIONS;
    return src
        .filter((s) => (s.show ? !!s.show(user) : true))
        .map((s) => {
            // A `sub` caption covers the items after it, so hiding its first item keeps the caption.
            let sub;
            const items = s.items.map((it) => { if (it.sub) sub = it.sub; return sub && !it.sub ? { ...it, sub } : it; });
            return { ...s, items: items.filter((it) => itemOk(user, it)) };
        })
        .filter((s) => s.items.length > 0);
}

// ---- "Not available for your login" (agent access review, 24 Oct 2026) ----
// An agent who types or follows the address of a page their menu hides gets a friendly notice
// instead of a page that fails on its first request. The list is derived from the menu itself:
// a page an agent login cannot see in SECTIONS is a page it is told is not available. Detail
// pages under a hidden roster stay open (a colleague's card from the directory or production
// report is the directory view the server gives). Other logins are left as they are.
const OPEN_DETAIL_PREFIXES = ['/dashboard/agents/', '/dashboard/employees/'];

/** True when this page is hidden from this login's menu and should show the notice instead. */
export function pageBlockedFor(user, pathname) {
    // Agent logins (24 Oct 2026) and, since 26 Oct 2026, any login with a job profile.
    if (!isAgentLogin(user) && !hasJobProfile(user)) return false;
    const path = String(pathname || '').replace(/\/+$/, '') || '/';
    if (path === '/dashboard') return false;
    const visible = new Set();
    const hidden = new Set();
    for (const s of SECTIONS) {
        const sectionOk = s.show ? !!s.show(user) : true;
        for (const it of s.items) {
            const p = it.to.split('?')[0];
            if (sectionOk && itemOk(user, it)) visible.add(p); else hidden.add(p);
        }
    }
    if (visible.has(path)) return false;
    if (OPEN_DETAIL_PREFIXES.some((pre) => path.startsWith(pre))) return false;
    for (const p of hidden) {
        if (visible.has(p) || p === '/dashboard') continue;
        if (path === p || path.startsWith(`${p}/`)) return true;
    }
    return false;
}

/** Menu search: every word typed must appear in the item's label or keywords. */
export function filterSections(sections, query) {
    const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return sections;
    return sections
        .map((s) => ({
            ...s,
            items: s.items.filter((it) => {
                const hay = `${it.label} ${it.keywords || ''}`.toLowerCase();
                return words.every((w) => hay.includes(w));
            }),
        }))
        .filter((s) => s.items.length > 0);
}

/** Title of the section holding the current route (longest match wins), or null. */
export function activeSectionTitle(sections, pathname, search) {
    const tab = new URLSearchParams(search || '').get('tab');
    let best = null;
    let bestScore = -1;
    for (const s of sections) {
        for (const it of s.items) {
            const [path, query] = it.to.split('?');
            const hit = pathname === path || (!it.end && pathname.startsWith(path + '/'));
            if (!hit) continue;
            const qTab = query ? new URLSearchParams(query).get('tab') : null;
            const score = path.length * 2 + (qTab && qTab === tab ? 1 : 0);
            if (score > bestScore) { bestScore = score; best = s.title; }
        }
    }
    return best;
}

const STORE_KEY = 'brec.nav.open';

/** Remembered open/closed sections: { [title]: boolean }. Never throws. */
export function readNavOpen(storage) {
    try {
        const raw = storage && storage.getItem(STORE_KEY);
        const v = raw ? JSON.parse(raw) : {};
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    } catch (e) { return {}; }
}

export function writeNavOpen(state, storage) {
    try { if (storage) storage.setItem(STORE_KEY, JSON.stringify(state || {})); } catch (e) { /* ignore */ }
}

/** A section is open if the user chose so; otherwise only when it holds the current page. */
export function isSectionOpen(title, openState, activeTitle) {
    const manual = openState ? openState[title] : undefined;
    return typeof manual === 'boolean' ? manual : activeTitle === title;
}

// ---- Arrange menu (owner, 5 Oct 2026): each login may put the sections in its own order ----
// The order is a list of section titles, saved per login (/api/ui-prefs/nav-order) and kept
// in this browser too so the menu shows in your order at once. Titles not in the list (a new
// section, or one renamed) keep their standard place after the listed ones; unknown titles
// are ignored. Ordering never changes which sections or items a login may see.

const ORDER_KEY = 'brec.nav.order';

/** The sections in the saved order. */
export function applyNavOrder(sections, order) {
    if (!Array.isArray(order) || order.length === 0) return sections;
    const rank = new Map(order.map((t, i) => [t, i]));
    return sections
        .map((s, i) => ({ s, i }))
        .sort((a, b) => {
            const ra = rank.has(a.s.title) ? rank.get(a.s.title) : order.length + a.i;
            const rb = rank.has(b.s.title) ? rank.get(b.s.title) : order.length + b.i;
            return ra - rb;
        })
        .map((x) => x.s);
}

/** A new order (list of titles) with `title` moved: 'up', 'down', 'top' or 'bottom'. */
export function moveSection(titles, title, where) {
    const list = titles.slice();
    const i = list.indexOf(title);
    if (i < 0) return list;
    list.splice(i, 1);
    let j = i;
    if (where === 'up') j = Math.max(0, i - 1);
    else if (where === 'down') j = Math.min(list.length, i + 1);
    else if (where === 'top') j = 0;
    else if (where === 'bottom') j = list.length;
    list.splice(j, 0, title);
    return list;
}

export function readNavOrder(storage) {
    try {
        const v = JSON.parse((storage && storage.getItem(ORDER_KEY)) || '[]');
        return Array.isArray(v) ? v.filter((t) => typeof t === 'string') : [];
    } catch (e) { return []; }
}

export function writeNavOrder(order, storage) {
    try { if (storage) storage.setItem(ORDER_KEY, JSON.stringify(order || [])); } catch (e) { /* ignore */ }
}
